import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';

const Placeholder = styled.span<{ size: number }>`
  display: inline-block;
  height: ${({ size }) => size}px;
  width: ${({ size }) => size}px;
`;

interface AsyncIconProps {
  className?: string;
  name: string;
  showOutline?: boolean;
  size?: number;
}

export function AsyncIcon({
  className,
  name,
  showOutline = false,
  size,
}: AsyncIconProps): JSX.Element {
  const mounted = useRef(true);
  const [iconUrl, setIconUrl] = useState<string | undefined>(undefined);

  // NOTE: the whole idea of having icon URL as a state is because import() that allows importing dynamic paths
  // is asynchronous and you cannot have async code in the component body; <img src={(await import(...)).default} />
  // is simply illegal because the component cannot be an async function; thus the effect that does async work and
  // the state that can be written from within it and read in the regular sync "markup"
  useEffect(() => {
    (async () => {
      const iconModule = await (showOutline
        ? import(`../images/outline/${name}.svg`)
        : import(`../images/plain/${name}.svg`));

      if (mounted.current) {
        // NOTE: setting state on unmounted component is big nonono for React
        setIconUrl(iconModule.default);
      }
    })();
    return () => {
      mounted.current = false;
    };
  }, [name, showOutline]);

  if (iconUrl === undefined) {
    return <Placeholder size={size ?? 0} />;
  }

  return (
    <img
      alt={name}
      aria-label={name}
      className={className}
      height={size}
      src={iconUrl}
      width={size}
    />
  );
}
